import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, AlertCircle, Loader2 } from 'lucide-react';
import { getSession } from '../../lib/auth';
import { uploadAudioFile } from '../../lib/api';
import { audioCache } from '../../utils/audioCache';

interface Props {
  onUploadComplete: () => void;
}

export function AudioUploader({ onUploadComplete }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const processFile = async (file: File) => {
    try {
      setError(null);
      setIsUploading(true);

      if (file.size > 52428800) {
        throw new Error('File size must be less than 50MB');
      }

      const session = getSession();

      if (session?.user) {
        await uploadAudioFile(file);
      } else {
        const isAvailable = await audioCache.isStorageAvailable();
        if (!isAvailable) {
          throw new Error(
            'Audio storage is not available in this session. Please use a regular browser window.',
          );
        }

        await audioCache.saveToCache(file, file.name);
      }

      onUploadComplete();
    } catch (error) {
      console.error('Error processing file:', error);
      setError(error instanceof Error ? error.message : 'Error processing file');
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      processFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.ogg', '.m4a'],
    },
    maxFiles: 1,
    disabled: isUploading,
    maxSize: 52428800,
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors ${
          isUploading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <input {...getInputProps()} />
        {isUploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Uploading...</p>
          </div>
        ) : (
          <>
            <Upload className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {isDragActive
                ? 'Drop your audio file here...'
                : 'Drag & drop an audio file, or click to select'}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Supported formats: MP3, WAV, OGG, M4A (max 50MB)
            </p>
          </>
        )}
      </div>
      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}
